import { ReactNode, useState } from 'react'
import styled, { css } from 'styled-components'
import ConfettiExplosion from 'react-confetti-explosion'
import { useSelector } from 'react-redux'
import { Box } from '../../../UI/Box'
import { theseus } from '../../../../api/theseus'
import { selectCurrentProject } from '../../../../modules/missionControl/selectors'
import { Checkbox } from '../../../../page_components/DropDetails/ChapterView'
import TaskBody from '../../TaskBody'
import { NewTaskType, SubTask, VerifyWalletMetaData } from '../../types'
import AnimatedLoader from '../../../AnimatedLoader'
import { ThemedButton } from '../../../ThemedButton'
import { useSearchParams } from '../../../../hooks/useSearchParams'
import { useDynamicContext } from '@dynamic-labs/sdk-react-core'

const CheckContainer = styled(Box)<{ isVisible: boolean }>`
  max-width: 0px;
  overflow: hidden;
  opacity: 0;
  transition: 0.3s ease-in-out;
  ${({ isVisible }) =>
    isVisible &&
    css`
      opacity: 1;
      max-width: 28px;
      padding-right: 8px;
    `}
`

export type Props = {
  subtask: SubTask
  taskId: number
  updateTaskSubmissions: () => Promise<unknown>
  taskAmount: ReactNode
}

type WalletConnectProvider = {
  isCoinbaseWallet?: boolean
  signer: {
    session: {
      peer: {
        metadata: {
          name: string
          url: string
        }
      }
    }
  }
  isBitKeep?: boolean
  isBitKeepChrome?: boolean
}

const WalletOptions = {
  BURRITO: 'Burrito',
  COINBASE: 'Coinbase',
  BITGET: 'Bitget',
  BYBIT: 'Bybit',
}

const VerifyWalletTask = ({
  subtask,
  taskId,
  updateTaskSubmissions,
  taskAmount,
}: Props) => {
  const { publicName, instructions } = subtask
  const title = publicName ?? 'Validate an on-chain action'
  const description =
    instructions ??
    'Simply press the verify button below to validate on-chain action'

  const [isLoadingVerify, setIsLoadingVerify] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isComplete, setIsComplete] = useState(false)

  const { primaryWallet } = useDynamicContext()

  const project = useSelector(selectCurrentProject)

  const { updateSearchParam } = useSearchParams()

  function selfDestruct(ms = 2000) {
    setTimeout(() => {
      updateSearchParam('active_mission', '')
    }, ms)
  }

  function verifyWalletConnected(walletType: string) {
    if (walletType === WalletOptions.BURRITO) {
      if (primaryWallet?.connector?.isWalletConnect) {
        return false
      }

      const isBurritoWallet = primaryWallet?.key?.includes('burrito')
      if (!isBurritoWallet) return false
    }

    if (walletType === WalletOptions.COINBASE) {
      if (!primaryWallet?.key?.includes('coinbase')) {
        return false
      }
    }

    if (walletType === WalletOptions.BITGET) {
      if (!primaryWallet?.key?.includes('bitget')) {
        return false
      }
    }

    if (walletType === WalletOptions.BYBIT) {
      if (!primaryWallet?.key?.includes('bybit')) {
        return false
      }
    }

    return true
  }

  async function validate() {
    try {
      setError(null)
      setIsLoadingVerify(true)

      const errorMessage =
        "We couldn't verify your wallet - please ensure you're connected to the right wallet and try again"

      if (
        subtask?.newTaskType !== NewTaskType.VERIFY_WALLET &&
        'walletType' in subtask?.metaData
      ) {
        throw new Error(errorMessage)
      }

      const { walletType, wallets } = subtask?.metaData as VerifyWalletMetaData

      if (!walletType && !wallets.length) {
        throw new Error(errorMessage)
      }

      if (wallets?.length) {
        const isValidWallet = wallets.some((wallet) =>
          verifyWalletConnected(wallet),
        )
        if (!isValidWallet) {
          throw new Error(errorMessage)
        }
      }

      if (walletType) {
        if (!verifyWalletConnected(walletType)) {
          throw new Error(errorMessage)
        }
      }

      const { data } = await theseus.post(
        `/task-submission/b2c/complete-task-submission`,
        {
          subtaskId: subtask.id,
          projectId: project.id,
          taskId,
        },
      )

      if (!data?.message) throw new Error(errorMessage)
      const { message } = data
      if (!message) throw new Error('Bad response')
      if (message !== 'success') throw new Error(message)
      await updateTaskSubmissions()
      selfDestruct()
      setIsComplete(true)
    } catch (error) {
      console.error(error?.message)
      setError(error.message)
    } finally {
      setIsLoadingVerify(false)
    }
  }

  return (
    <TaskBody
      taskId={taskId}
      title={title}
      description={description}
      error={error}
      extra={taskAmount}
      actions={
        <Box fu aw>
          <ThemedButton
            onClick={validate}
            disabled={isComplete || isLoadingVerify}
            $styleType="primary"
          >
            {isComplete && <ConfettiExplosion zIndex={10000} />}
            <CheckContainer isVisible={isComplete}>
              <Checkbox />
            </CheckContainer>
            {isLoadingVerify ? (
              <AnimatedLoader size={14} />
            ) : isComplete ? (
              'Verified'
            ) : (
              'Verify'
            )}
          </ThemedButton>
        </Box>
      }
    />
  )
}

export default VerifyWalletTask
